import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';

// Define the enum for combo box mode
export enum ComboBoxMode {
    Standard = 1,
    SearchableOneSelect,
    SearchableMultipleSelect,
}

interface Option {
    value: string;
    label: string;
}

interface ComboBoxProps {
    label: string;
    inputId: string;
    required?: boolean;
    errorMessage?: string;
    options: Option[];
    value: string | string[]; // Allow array for multiple select
    onChange: (value: string | string[]) => void; // Adjusted to directly receive values
    mode?: ComboBoxMode; // Add mode prop
    isLongTextToNextLine?: boolean; // Optional parameter for text wrapping
}

interface ComboBoxHandle {
    validate: () => boolean;
    showError: () => void;
    hideError: () => void;
    getValue: () => string | string[];
    setValue: (value: string | string[]) => void; // Support multiple values
}

// Türkçe karakter uyumlu küçük harfe çevirme fonksiyonu
const turkishToLower = (str: string) => {
    const letters: { [key in 'İ' | 'I' | 'Ş' | 'Ğ' | 'Ü' | 'Ö' | 'Ç']: string } = {
        'İ': 'i',
        'I': 'ı',
        'Ş': 'ş',
        'Ğ': 'ğ',
        'Ü': 'ü',
        'Ö': 'ö',
        'Ç': 'ç'
    };
    return str.replace(/[\u0130IŞĞÜÖÇ]/g, (match) => letters[match as keyof typeof letters]).toLowerCase();
};

const ComboBox = forwardRef<ComboBoxHandle, ComboBoxProps>(({
    label,
    inputId,
    required = false,
    errorMessage,
    options = [],
    value = '',
    onChange,
    mode = ComboBoxMode.Standard, // Default to Standard mode
    isLongTextToNextLine = false, // Default to false
}, ref) => {
    const [isValid, setIsValid] = useState(true);
    const [inputValue, setInputValue] = useState<string | string[]>(value || ''); // Initialize state with value prop
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1); // Track the highlighted option
    const comboBoxRef = useRef<HTMLDivElement | null>(null);

    // Sync state with prop value
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    useImperativeHandle(ref, () => ({
        validate: () => {
            if (required && ((typeof inputValue === 'string' && (inputValue === "0" || !inputValue)) || (Array.isArray(inputValue) && inputValue.length === 0))) {
                setIsValid(false);
                return false;
            }
            setIsValid(true);
            return true;
        },
        showError: () => {
            setIsValid(false);
        },
        hideError: () => {
            setIsValid(true);
        },
        getValue: () => inputValue,
        setValue: (value: string | string[]) => {
            setInputValue(value || '');
        },
    }));

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (comboBoxRef.current && !comboBoxRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        // Cleanup event listener on component unmount
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    const filteredOptions = options.filter(option =>
        turkishToLower(option.label).includes(turkishToLower(searchTerm))
    );

    const handleOptionClick = (selectedValue: string) => {
        if (mode === ComboBoxMode.SearchableMultipleSelect) {
            const newValue = Array.isArray(inputValue)
                ? inputValue.includes(selectedValue)
                    ? inputValue.filter(v => v !== selectedValue)
                    : [...inputValue, selectedValue]
                : [selectedValue];

            setInputValue(newValue);
            onChange(newValue);
        } else {
            setInputValue(selectedValue);
            onChange(selectedValue);
            setDropdownOpen(false); // Close dropdown after selection
        }
    };

    const handleDropdownToggle = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation(); // Prevent click from bubbling
        setDropdownOpen((prevOpen) => !prevOpen);
        setSearchTerm(''); // Clear search term when dropdown is opened
        setHighlightedIndex(-1); // Reset highlighted index when reopening the dropdown
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!dropdownOpen) return;

        switch (event.key) {
            case 'ArrowDown':
                setHighlightedIndex((prevIndex) =>
                    prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : 0
                );
                break;
            case 'ArrowUp':
                setHighlightedIndex((prevIndex) =>
                    prevIndex > 0 ? prevIndex - 1 : filteredOptions.length - 1
                );
                break;
            case 'Enter':
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleOptionClick(filteredOptions[highlightedIndex].value);
                }
                break;
            case 'Escape':
                setDropdownOpen(false);
                break;
            default:
                break;
        }
    };

    const isSelected = (value: string) => {
        if (Array.isArray(inputValue)) {
            return inputValue.includes(value);
        }
        return inputValue === value;
    };

    const getDisplayLabel = (value: string | string[]) => {
        if (Array.isArray(value)) {
            return value.map(val => options.find(option => option.value === val)?.label || val).join(', ');
        }
        return options.find(option => option.value === value)?.label || 'Select...';
    };

    return (
        <div
            className="form-group"
            style={{ position: 'relative' }}
            ref={comboBoxRef}
            onKeyDown={handleKeyDown} // Handle keyboard events
            tabIndex={0} // Make div focusable to capture keyboard events
        >
            <label className="form-label" htmlFor={inputId}>
                {label}
            </label>
            <div
                className={`form-control mb-0 ${!isValid ? 'is-invalid' : ''}`}
                style={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: isLongTextToNextLine ? 'unset' : 'ellipsis',
                    whiteSpace: isLongTextToNextLine ? 'normal' : 'nowrap',
                }}
                onClick={handleDropdownToggle}
                data-value={inputValue} // Add the data-value attribute
            >
                {getDisplayLabel(inputValue)}
            </div>
            {dropdownOpen && (
                <div
                    className="dropdown-menu show"
                    style={{
                        position: 'absolute',
                        zIndex: 1,
                        width: '100%',
                        maxHeight: '200px', // Limit the height for scrolling
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                    }}
                >
                    {(mode === ComboBoxMode.SearchableOneSelect || mode === ComboBoxMode.SearchableMultipleSelect) && (
                        <input
                            type="text"
                            className="form-control mb-0"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ marginBottom: '10px' }}
                            autoFocus // Automatically focus the search input
                        />
                    )}
                    {filteredOptions.map((option, index) => (
                        <div
                            key={index}
                            className={`dropdown-item ${isSelected(option.value) ? 'active' : ''} ${highlightedIndex === index ? 'highlighted' : ''}`}
                            onClick={() => handleOptionClick(option.value)}
                            style={{
                                cursor: 'pointer',
                                overflow: isLongTextToNextLine ? 'unset' : 'hidden',
                                textOverflow: isLongTextToNextLine ? 'unset' : 'ellipsis',
                                whiteSpace: isLongTextToNextLine ? 'normal' : 'nowrap',
                                backgroundColor: highlightedIndex === index ? '#007bff' : 'transparent',
                                color: highlightedIndex === index ? '#fff' : 'inherit'
                            }}
                            data-value={option.value} // Add data-value attribute to each option
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
            {!isValid && (
                <div className="invalid-feedback">
                    {errorMessage}
                </div>
            )}
        </div>
    );
});

export default ComboBox;
